import { AuthAPI } from '@/api/auth/auth.api';
import { clearAccessToken, setAccessToken } from '@/lib/client';
import { useAuthStore } from '@/store/Auth';
import {
  createFileRoute,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { z } from 'zod';

const searchSchema = z.object({
  token: z.string().optional(),
  refreshToken: z.string().optional(),
  error: z.string().optional(),
});

export const Route = createFileRoute('/admin/auth/success')({
  validateSearch: searchSchema,
  component: AdminAuthSuccessPage,
});

function AdminAuthSuccessPage() {
  const navigate = useNavigate();
  const { token, refreshToken, error } = useSearch({
    from: '/admin/auth/success',
  });
  const { setAuth } = useAuthStore();
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    const handleAuth = async () => {
      if (error) {
        navigate({
          to: '/login',
          search: { error: encodeURIComponent(error) },
          replace: true,
        });
        return;
      }

      if (!token) {
        navigate({
          to: '/login',
          search: { error: 'missing_token' },
          replace: true,
        });
        return;
      }

      if (!globalThis.window) {
        navigate({
          to: '/login',
          search: { error: 'window_undefined' },
          replace: true,
        });
        return;
      }

      setAccessToken(token);

      if (refreshToken) {
        globalThis.localStorage.setItem('jumbo-refresh', refreshToken);
      }

      // Fetch admin details and populate UI auth store
      try {
        const response = await AuthAPI.getAdminDetails.fn();
        const admin = response.data;

        setAuth({
          user: {
            id: admin.id.toString(),
            email: admin.email,
            name: admin.fullName,
            image: admin.profileImgUrl,
            role: admin.role,
          },
          expires: '',
        });

        navigate({ to: '/dashboard', replace: true });
      } catch (err: unknown) {
        const error = err as { response?: { status?: number } };
        if (error?.response?.status === 401) {
          clearAccessToken();
          navigate({ to: '/login', replace: true });
        } else {
          console.error('Error fetching admin details:', err);
          navigate({
            to: '/login',
            search: { error: 'fetch_failed' },
            replace: true,
          });
        }
      } finally {
        setIsProcessing(false);
      }
    };

    handleAuth();
  }, [token, refreshToken, error, navigate, setAuth]);

  return (
    <main className='flex min-h-screen items-center justify-center'>
      <p className='text-sm text-muted-foreground'>
        {isProcessing ? 'Processing authentication...' : 'Redirecting...'}
      </p>
    </main>
  );
}
